import { Autocomplete } from '@components/Autocomplete';
import { Checkbox } from '@components/Checkbox';
import {
  Box,
  Button,
  ClickAwayListener,
  Paper,
  PaperProps,
  Popper,
  PopperProps,
  Stack,
  Typography,
} from '@mui/material';
import { IProductSearch, selectionsApiSlice } from '@src/api';
import { theme } from '@src/theme';
import { buildDataTestId, buildDataTestIdAttribute, GUID_EMTY, hexOpacity, scrollBarSM } from '@src/utils';
import { ChangeEvent, FC, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { ReactComponent as PopupIcon } from '@icons/popupIcon.svg';
import { useLoadingContext } from '../ContentLoading';

type ProductItemProps = {
  product: IProductSearch;
  checked: boolean;
  onChange: ({ checked, value }: { checked: boolean; value: IProductSearch }) => void;
};

const ProductItem: FC<ProductItemProps> = memo(({ product, checked, onChange }) => {
  const { name, code } = product;

  const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked: checkedEvent } = event.target;
    onChange({ checked: checkedEvent, value: product });
  };

  return (
    <Stack direction="row" gap="12px" padding="12px">
      <Checkbox checked={checked} onChange={handleOnChange} sx={{ padding: 0 }} />
      <Typography color="grey.700" variant="label14Medium" lineHeight="18px">{`${name} (Code: ${code})`}</Typography>
    </Stack>
  );
});

export const PaperComponent = memo(
  ({
    children,
    handleCancel,
    dataTestId,
    products,
    open,
    ...paperProps
  }: PaperProps & {
    handleCancel: () => void;
    products?: IProductSearch[];
    dataTestId?: string;
    open: boolean;
  }) => {
    const { setValue, watch } = useFormContext();
    const [additionalProductIds] = watch(['additionalProductIds']);

    const [localChecked, setLocalChecked] = useState<IProductSearch[]>([]);

    const handleApply = () => {
      setValue('additionalProductIds', localChecked, { shouldValidate: true });
      handleCancel();
    };
    useEffect(() => {
      if (additionalProductIds) setLocalChecked(additionalProductIds as IProductSearch[]);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const onChangeChecked = useCallback(({ checked, value }: { checked: boolean; value: IProductSearch }) => {
      setLocalChecked(prev => (checked ? [...prev, value] : prev.filter(({ id }) => id !== value.id)));
    }, []);

    return (
      <Box height="100%" sx={{ '& .MuiPaper-root': { overflow: 'hidden' } }}>
        <ClickAwayListener onClickAway={handleCancel}>
          <Paper
            sx={{
              boxShadow: '0px 8px 12px rgba(55, 65, 81, 0.1), 0px 0px 6px rgba(55, 65, 81, 0.1)',
              height: '100%',
              zIndex: 1,
            }}
            {...paperProps}
          >
            <Stack position="relative" overflow="auto" height="calc(100% - 55px)" sx={scrollBarSM}>
              {products?.map(product => {
                const checked = !!localChecked.some(({ id }) => id === product.id);
                return <ProductItem key={product.id} product={product} checked={checked} onChange={onChangeChecked} />;
              })}
            </Stack>

            <Stack
              padding="12px"
              direction="row"
              justifyContent="space-between"
              position="absolute"
              bottom="0"
              width="100%"
              borderTop={`1px solid ${hexOpacity(theme.palette.secondary.main, '14%')}`}
              zIndex={20}
              bgcolor="#FFFFFF"
              boxSizing="border-box"
            >
              <Button
                disabled={false}
                color="secondary"
                size="small"
                variant="outlined"
                sx={{ width: '78px', minWidth: '78px', padding: 0, height: '30px', fontWeight: 500, fontSize: '14px' }}
                onClick={handleCancel}
                {...buildDataTestIdAttribute(dataTestId, 'button-cancel')}
              >
                Cancel
              </Button>

              <Button
                color="secondary"
                variant="contained"
                size="small"
                sx={{ width: '78px', minWidth: '78px', padding: 0, height: '30px', fontWeight: 500, fontSize: '14px' }}
                onClick={handleApply}
                {...buildDataTestIdAttribute(dataTestId, 'button-apply')}
              >
                Apply
              </Button>
            </Stack>
          </Paper>
        </ClickAwayListener>
      </Box>
    );
  },
);

export const SelectTests = memo(() => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const { watch } = useFormContext();
  const { setLoadingState } = useLoadingContext() || {};

  const [laboratoryId, providerId] = watch(['laboratoryId', 'providerId']);

  const { data: products, isSuccess } = selectionsApiSlice.useGetAdditionalOrdersProductsQuery(
    {
      search,
      laboratoryId,
      providerMaintenanceId: providerId === GUID_EMTY ? null : providerId,
    },
    {
      skip: !laboratoryId,
    },
  );

  const customPopper = useMemo(
    () =>
      ({ children, ...popperProps }: PopperProps) => {
        return (
          <Popper
            sx={{
              height: '348px',
              boxShadow: '0px 8px 12px rgba(55, 65, 81, 0.1), 0px 0px 6px rgba(55, 65, 81, 0.1)',
            }}
            {...popperProps}
            modifiers={[
              {
                name: 'flip',
                options: {
                  allowedAutoPlacements: ['top', 'bottom'],
                },
              },
              {
                name: 'offset',
                options: {
                  offset: [0, 2],
                },
              },
            ]}
            placement="bottom"
          >
            {children}
          </Popper>
        );
      },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const handleChange = (_e: ChangeEvent<HTMLInputElement>, value: string, reason) => {
    if (reason === 'blur' || reason === 'reset') return;
    setSearch(value || '');
  };

  const resultDataTestId = buildDataTestId('create-order-modal', 'input-tests-search');

  const handleCancel = useCallback(() => {
    setOpen(false);
    setSearch('');
  }, [setOpen, setSearch]);

  const onClick = () => {
    if (!laboratoryId) return;
    setOpen(true);
  };

  useEffect(() => {
    if (isSuccess && setLoadingState) setLoadingState(prev => ({ ...prev, tests: true }));
  }, [isSuccess, setLoadingState]);

  return (
    <Autocomplete
      showCustomClearIcon
      renderOption={() => <></>}
      open={open}
      onClose={() => {}}
      PaperComponent={(props: PaperProps) => (
        <PaperComponent
          {...props}
          handleCancel={handleCancel}
          dataTestId={resultDataTestId}
          open={open}
          products={products}
        />
      )}
      popupIcon={<PopupIcon />}
      PopperComponent={customPopper}
      setState={{}}
      onClick={onClick}
      componentsProps={{ popper: { sx: { zIndex: 1 } } }}
      placeholder="Add additional tests"
      options={[]}
      multiple={undefined}
      value={undefined}
      loading={undefined}
      disableClearable={true}
      inputIsClearable={false}
      disabled={!laboratoryId}
      onChange={() => {}}
      withSearchIcon={false}
      sx={{
        '& .MuiFormControl-root': {
          '& .MuiInputBase-root': {
            padding: '12px',
            input: { height: '18px' },
            '&.Mui-disabled': {
              background: '#fff',
            },
          },
        },
      }}
      onInputChange={handleChange}
      inputValue={search}
      dataTestId={resultDataTestId}
    />
  );
});
